import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  listRefunds,
  listRefundsPortal,
  createRefund,
  createRefundPortal,
  updateRefundStatus,
  updateRefundStatusPortal
} from "../../api/fees/feeApi";
import {
  getAllStudents,
  getAllClasses,
  getAllSections
} from "../../api/student/studentApi";
import {
  getAllStudentsForPortal,
  getAllClassesForPortal,
  getAllSectionsForPortal
} from "../../api/student/studentPortalApi";
import {
  getAllAcademicYears,
  getAllAcademicYearsPortal
} from "../../api/academicyear/academicYearApi";
import {
  Skeleton
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const FeeRefundsPage = ({ isPortal = false }) => {
  const [rows, setRows] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classFilter, setClassFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [studentSearch, setStudentSearch] = useState("");
  const [form, setForm] = useState({
    studentId: "",
    academicYearId: "",
    refundType: "OVERPAYMENT",
    amount: "",
    reason: "",
    autoProcess: false,
  });

  const toast = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [refundRes, yearRes, classRes, studentRes] = await Promise.all([
        isPortal ? listRefundsPortal() : listRefunds(),
        isPortal ? getAllAcademicYearsPortal() : getAllAcademicYears(),
        isPortal ? getAllClassesForPortal() : getAllClasses(),
        isPortal ? getAllStudentsForPortal() : getAllStudents(),
      ]);
      setRows(refundRes.data || refundRes || []);
      const ys = yearRes?.data || yearRes || [];
      setYears(Array.isArray(ys) ? ys : []);
      const active = (Array.isArray(ys) ? ys : []).find(
        (y) => y.isActive || y.status === "ACTIVE" || y.current
      );
      setForm((f) => ({
        ...f,
        academicYearId: f.academicYearId || active?._id || (ys[0] && ys[0]._id) || "",
      }));
      setClasses(Array.isArray(classRes?.data || classRes) ? classRes.data || classRes : []);
      setStudents(Array.isArray(studentRes?.data || studentRes) ? studentRes.data || studentRes : []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to load refunds");
    } finally {
      setLoading(false);
    }
  }, [isPortal]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!classFilter) {
      setSections([]);
      return;
    }
    (isPortal ? getAllSectionsForPortal({ classId: classFilter }) : getAllSections({ classId: classFilter }))
      .then((res) => {
        const list = res?.data || res || [];
        setSections(Array.isArray(list) ? list : []);
      })
      .catch(() => setSections([]));
  }, [classFilter, isPortal]);

  const filteredStudents = useMemo(() => {
    let list = students || [];
    if (classFilter) {
      list = list.filter(
        (s) =>
          String(s.classId?._id || s.classId) === String(classFilter)
      );
    }
    if (sectionFilter) {
      list = list.filter(
        (s) =>
          String(s.sectionId?._id || s.sectionId) === String(sectionFilter)
      );
    }
    if (studentSearch.trim()) {
      const q = studentSearch.trim().toLowerCase();
      list = list.filter((s) => {
        const name = [s.firstName, s.middleName, s.lastName].filter(Boolean).join(" ").toLowerCase();
        const adm = String(s.admissionNo || "").toLowerCase();
        return name.includes(q) || adm.includes(q);
      });
    }
    return list.slice(0, 200);
  }, [students, classFilter, sectionFilter, studentSearch]);

  const studentLabel = (s) => {
    const name = [s.firstName, s.middleName, s.lastName].filter(Boolean).join(" ");
    return `${name} · ${s.admissionNo || s._id}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.studentId) {
      toast.error("Select a student");
      return;
    }
    if (!form.academicYearId) {
      toast.error("Academic year is required");
      return;
    }
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (isPortal) await createRefundPortal(payload);
      else await createRefund(payload);
      setForm((f) => ({
        ...f,
        studentId: "",
        refundType: "OVERPAYMENT",
        amount: "",
        reason: "",
        autoProcess: false,
      }));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Create failed");
    }
  };

  const setStatus = async (id, status) => {
    try {
      if (isPortal) await updateRefundStatusPortal(id, { status });
      else await updateRefundStatus(id, { status });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Status update failed");
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-semibold mb-4">Fee Refunds</h1>
      

      <form
        onSubmit={submit}
        className="bg-white dark:bg-neutral-900 border rounded-card p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3"
      >
        <div>
          <label className="block text-xs text-neutral-500 dark:text-neutral-400 mb-1">Academic year</label>
          <select
            required
            value={form.academicYearId}
            onChange={(e) => setForm({ ...form, academicYearId: e.target.value })}
            className="w-full border rounded px-3 py-2 text-sm"
          >
            <option value="">Select year…</option>
            {years.map((y) => (
              <option key={y._id} value={y._id}>
                {y.name || y.yearName || y._id}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs text-neutral-500 dark:text-neutral-400 mb-1">Filter class</label>
          <select
            value={classFilter}
            onChange={(e) => {
              setClassFilter(e.target.value);
              setSectionFilter("");
            }}
            className="w-full border rounded px-3 py-2 text-sm"
          >
            <option value="">All classes</option>
            {classes.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name || c.className || c._id}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs text-neutral-500 dark:text-neutral-400 mb-1">Filter section</label>
          <select
            value={sectionFilter}
            onChange={(e) => setSectionFilter(e.target.value)}
            className="w-full border rounded px-3 py-2 text-sm"
            disabled={!classFilter}
          >
            <option value="">All sections</option>
            {sections.map((s) => (
              <option key={s._id} value={s._id}>
                {s.name || s.sectionName || s._id}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs text-neutral-500 dark:text-neutral-400 mb-1">Search student</label>
          <input
            type="text"
            placeholder="Name or admission no"
            value={studentSearch}
            onChange={(e) => setStudentSearch(e.target.value)}
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="block text-xs text-neutral-500 dark:text-neutral-400 mb-1">Student</label>
          <select
            required
            value={form.studentId}
            onChange={(e) => setForm({ ...form, studentId: e.target.value })}
            className="w-full border rounded px-3 py-2 text-sm"
          >
            <option value="">Select student…</option>
            {filteredStudents.map((s) => (
              <option key={s._id} value={s._id}>
                {studentLabel(s)}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-neutral-400 dark:text-neutral-500 mt-1">
            Showing {filteredStudents.length} student(s)
          </p>
        </div>

        <select
          value={form.refundType}
          onChange={(e) => setForm({ ...form, refundType: e.target.value })}
          className="border rounded px-3 py-2 text-sm"
        >
          <option value="WITHDRAWAL">Withdrawal</option>
          <option value="OVERPAYMENT">Overpayment</option>
          <option value="FEE_ADJUSTMENT">Fee adjustment</option>
        </select>
        <input
          required
          type="number"
          min="0.01"
          step="0.01"
          placeholder="Amount"
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
          className="border rounded px-3 py-2 text-sm"
        />
        <input
          required
          placeholder="Reason"
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
          className="border rounded px-3 py-2 text-sm sm:col-span-2"
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.autoProcess}
            onChange={(e) => setForm({ ...form, autoProcess: e.target.checked })}
          />
          Auto process
        </label>
        <button type="submit" className="px-4 py-2 bg-primary-900 text-white rounded text-sm">
          Create refund
        </button>
      </form>

      {loading ? (
                <div className="space-y-3 rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 shadow-card dark:shadow-black/20">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex gap-4">
              <Skeleton height={16} width="20%" />
              <Skeleton height={16} width="25%" />
              <Skeleton height={16} width="15%" />
              <Skeleton height={16} width="20%" />
            </div>
          ))}
        </div>
      ) : (
        <table className="w-full text-sm bg-white dark:bg-neutral-900 border rounded">
          <thead className="bg-neutral-50 dark:bg-neutral-950 text-left">
            <tr>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Amount</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Reason</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r._id} className="border-t">
                <td className="px-3 py-2">{r.refundType}</td>
                <td className="px-3 py-2">{r.amount}</td>
                <td className="px-3 py-2">{r.status}</td>
                <td className="px-3 py-2">{r.reason}</td>
                <td className="px-3 py-2 space-x-1">
                  {r.status === "PENDING" && (
                    <>
                      <button type="button" className="text-primary-900 dark:text-primary-300" onClick={() => setStatus(r._id, "APPROVED")}>
                        Approve
                      </button>
                      <button type="button" className="text-green-600" onClick={() => setStatus(r._id, "PROCESSED")}>
                        Process
                      </button>
                      <button type="button" className="text-danger-500" onClick={() => setStatus(r._id, "REJECTED")}>
                        Reject
                      </button>
                    </>
                  )}
                  {r.status === "APPROVED" && (
                    <button type="button" className="text-green-600" onClick={() => setStatus(r._id, "PROCESSED")}>
                      Process
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default FeeRefundsPage;
